import { useState } from 'react'

import { initRequestParam } from 'constants/constants'

import _ from 'lodash'
import { useCurrentStatusStore } from 'store/currentStatusStore'
import { getAPI } from 'utils/getAPI'

import type { TransportRelation, GetMstTransportRelationTransportRelationGetRequest, MasterTransportRelationUpdateMstTransportRelationUpdatePostRequest, MstTransportRelation, ResponseResult, ResponseUpdate } from 'api-client'
import type { ErrorResponse } from 'types/types'

export interface AutocompleteCustomer {
  label: string | null
  customerCompanyId: number | null
}

export interface MstTransportRelationSeq extends MstTransportRelation{
  id: string
}

export interface MstTransportRelationsSeq extends TransportRelation{
  list: MstTransportRelationSeq[]
}

export const useTransportRelation = () => {
  const [transportRelation, setTransportRelation] = useState<MstTransportRelationsSeq>(initRequestParam)
  const [autocompletCustomers, setAutocompleteCustomers] = useState<AutocompleteCustomer[]>([])

  const [result, setResult] = useState<ResponseResult | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [updateResult, setUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
 
  const [transportRelationSearch, setTransportRelationSearch] = useState<TransportRelation>(initRequestParam)
  const [transportRelationSearchIsLoading, setTransportRelationSearchIsLoading] = useState(false)
  const [transportRelationSearchResult, setTransportRelationSearchResult] = useState<ResponseResult | undefined>(undefined)
 
  const { setCurrentStatuses } = useCurrentStatusStore()

  const fetchTransportRelation = async (searchParam: GetMstTransportRelationTransportRelationGetRequest) => {
    setTransportRelationSearch(initRequestParam)
    setTransportRelationSearchResult(undefined)
    setTransportRelationSearchIsLoading(true)
    setIsLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.getMstTransportRelationTransportRelationGet(searchParam)

      setTransportRelationSearch({
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        }))
      })
      setTransportRelationSearchResult(response.result)
      setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setTransportRelationSearchResult({
        status: 'error',
        message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `輸送マスタの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return undefined
    } finally {
      setTransportRelationSearchIsLoading(false)
      setIsLoading(false)
    }
  }

  const fetchTransportRelationUpdate = async (body: MasterTransportRelationUpdateMstTransportRelationUpdatePostRequest): Promise<ResponseUpdate> => {
      setIsUpdating(true);
      setResult(undefined)
      // setCenterUpdateIsLoading(true);
      try {
        const api = await getAPI()
        if (!api) throw new Error('API initialization failed')
        const response = await api.masterTransportRelationUpdateMstTransportRelationUpdatePost(body);
        
        setResult(response.result)
        setUpdateResult(response);
  
        if (response.result.status !== 'success') {
          const error = new Error(response.result.message || '入力エラーがあります');
          Object.assign(error, {
              ...response
          });
          throw error;
        }
        return response;
      } catch (error) {
          if (error && typeof error === 'object' && 'result' in error) {
              const apiResponse = error as ResponseUpdate; 
              await new Promise<void>(resolve => {
                setUpdateResult(apiResponse);
                resolve();
              });
              return await Promise.reject(apiResponse);
          }
          const { response } = error as any;
          const errorResponse = {
              errors: [],
              result: {
                  status: 'error',
                  message: response && response.status === 422 ? '入力値に誤りがあります。' : `輸送マスタの更新でエラーが発生しました。`,
                  systemDate: null,
                  pageNo: 0,
                  systemInfo: null,
              },
              updateCount: null,
              lastUpdateDatetime: null,
          };
          await new Promise<void>(resolve => {
            setUpdateResult(errorResponse);
              setResult(errorResponse.result)
              resolve();
          });
          return await Promise.reject(errorResponse);
  
      } finally {
        // setCenterUpdateIsLoading(false);
        setIsUpdating(false);
      }
    };

  return {
    transportRelation,
    autocompletCustomers,

    fetchTransportRelation,
    transportRelationSearch,
    transportRelationSearchResult,
    transportRelationSearchIsLoading,

    result,
    fetchTransportRelationUpdate,
    updateResult,
    isUpdating
  }
}
